import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { Users, UserCheck, ChevronRight, TrendingUp, DollarSign, Clock, GitCompare, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { getPayTypeColor } from '../utils/payTypeColors';

const LEVEL_COLORS = {
  level_1: '#2E5C8A',  // Brand blue for L1
  level_2: '#1e3a5f',  // Navy for L2
  level_3: '#4CAF93',  // Green for L3
};

const COMPARE_COLORS = ['#2E5C8A', '#FF8C42', '#4CAF93', '#9333EA'];

const SupervisorAnalysis = ({ onDrillDown }) => {
  const { API } = React.useContext(AppContext);
  const { filters } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeLevel, setActiveLevel] = useState('level_1');
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  // Create stable filter key for dependency
  const filterKey = JSON.stringify({
    start_date: filters?.start_date || null,
    end_date: filters?.end_date || null,
    location: filters?.location || null,
    department: filters?.department || null,
    manager: filters?.manager || null,
    pay_types: filters?.pay_types || []
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/analytics/supervisor`, {
          start_date: filters?.start_date || undefined,
          end_date: filters?.end_date || undefined,
          location: filters?.location || undefined,
          department: filters?.department || undefined,
          manager: filters?.manager || undefined,
          pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching supervisor data:', error);
        toast.error('Failed to load supervisor analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API, filterKey]);

  // Clear selections when changing levels
  useEffect(() => {
    setSelectedSupervisors([]);
    setShowComparison(false);
  }, [activeLevel]);

  const handleBarClick = (data) => {
    if (data?.supervisor_name && onDrillDown) {
      const filterTypeMap = {
        level_1: 'supervisor_l1',
        level_2: 'supervisor_l2',
        level_3: 'supervisor_l3'
      };
      onDrillDown({
        type: filterTypeMap[activeLevel] || 'manager',
        value: data.supervisor_name
      });
    }
  };

  const handleCheckboxChange = (supervisor) => {
    setSelectedSupervisors(prev => {
      const exists = prev.find(s => s.supervisor_id === supervisor.supervisor_id);
      if (exists) {
        return prev.filter(s => s.supervisor_id !== supervisor.supervisor_id);
      }
      if (prev.length >= 4) {
        toast.warning('You can compare up to 4 supervisors');
        return prev;
      }
      return [...prev, { ...supervisor, level: activeLevel }];
    });
  };

  const isSelected = (supervisorId) => {
    return selectedSupervisors.some(s => s.supervisor_id === supervisorId);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const breakdown = (data.premium_breakdown || []).filter(b => b.hours > 0);
      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-4 min-w-[240px]">
          <p className="font-bold text-[#1e3a5f] mb-1">{label}</p>
          {data.supervisor_title && (
            <p className="text-sm text-muted-foreground mb-2 border-b pb-2">{data.supervisor_title}</p>
          )}
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Premium Hours:</span>
              <span className="font-medium">{(data.total_premium_hours ?? data.overtime_hours)?.toLocaleString()}h</span>
            </div>
            {breakdown.length > 0 && (
              <div className="border-t pt-1.5 mt-1.5 space-y-1 text-xs">
                {breakdown.map(b => (
                  <div key={b.pay_type} className="flex justify-between">
                    <span style={{ color: getPayTypeColor(b.pay_type) }}>{b.pay_type} ({b.multiplier}x):</span>
                    <span>{b.hours.toLocaleString()}h</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between border-t pt-1.5 mt-1.5">
              <span className="text-muted-foreground">Premium Cost:</span>
              <span className="font-medium text-green-600">${data.cost?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Employees:</span>
              <span className="font-medium">{data.employee_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Avg/Employee:</span>
              <span className="font-medium">{data.avg_overtime_per_employee?.toFixed(1)}h</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">Click to view direct reports</p>
        </div>
      );
    }
    return null;
  };

  const renderComparisonView = () => {
    if (selectedSupervisors.length < 2) return null;

    // Prepare data for comparison chart
    const comparisonData = [
      {
        metric: 'OT Hours',
        ...selectedSupervisors.reduce((acc, sup, idx) => {
          acc[`sup_${idx}`] = sup.overtime_hours;
          return acc;
        }, {})
      },
      {
        metric: 'Cost ($K)',
        ...selectedSupervisors.reduce((acc, sup, idx) => {
          acc[`sup_${idx}`] = Math.round(sup.cost / 1000);
          return acc;
        }, {})
      },
      {
        metric: 'Employees',
        ...selectedSupervisors.reduce((acc, sup, idx) => {
          acc[`sup_${idx}`] = sup.employee_count;
          return acc;
        }, {})
      },
      {
        metric: 'Avg/Emp (h)',
        ...selectedSupervisors.reduce((acc, sup, idx) => {
          acc[`sup_${idx}`] = Math.round(sup.avg_overtime_per_employee);
          return acc;
        }, {})
      }
    ];

    const levelLabels = {
      level_1: 'L1',
      level_2: 'L2',
      level_3: 'L3'
    };

    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-blue-200 p-5 mb-6"
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-[#2E5C8A]" />
            <h4 className="font-semibold text-[#1e3a5f]">Supervisor Comparison</h4>
            <span className="text-sm text-muted-foreground">({selectedSupervisors.length} selected)</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowComparison(false);
              setSelectedSupervisors([]);
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>

        {/* Selected Supervisors Legend */}
        <div className="flex flex-wrap gap-3 mb-4">
          {selectedSupervisors.map((sup, idx) => (
            <div
              key={sup.supervisor_id}
              className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border shadow-sm"
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: COMPARE_COLORS[idx] }}
              />
              <span className="text-sm font-medium">{sup.supervisor_name}</span>
              <span className="text-xs text-muted-foreground">({levelLabels[sup.level]})</span>
              <button
                onClick={() => handleCheckboxChange(sup)}
                className="text-muted-foreground hover:text-red-500 ml-1"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>

        {/* Comparison Chart */}
        <div className="bg-white rounded-lg p-4 border">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={comparisonData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F0F8" />
              <XAxis type="number" tick={{ fill: '#1e3a5f', fontSize: 11 }} />
              <YAxis 
                dataKey="metric" 
                type="category" 
                width={80}
                tick={{ fill: '#1e3a5f', fontSize: 11 }}
              />
              <Tooltip />
              <Legend 
                formatter={(value, entry) => {
                  const idx = parseInt(value.split('_')[1]);
                  return selectedSupervisors[idx]?.supervisor_name || value;
                }}
              />
              {selectedSupervisors.map((sup, idx) => (
                <Bar
                  key={sup.supervisor_id}
                  dataKey={`sup_${idx}`}
                  fill={COMPARE_COLORS[idx]}
                  name={`sup_${idx}`}
                  radius={[0, 4, 4, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Comparison Table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground">Metric</th>
                {selectedSupervisors.map((sup, idx) => (
                  <th key={sup.supervisor_id} className="text-right py-2 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COMPARE_COLORS[idx] }}
                      />
                      <span className="font-semibold text-[#1e3a5f] truncate max-w-[120px]">
                        {sup.supervisor_name}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border">
                <td className="py-2 px-3 text-muted-foreground">Title</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right text-sm">
                    {sup.supervisor_title || '-'}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 text-muted-foreground">Level</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right">
                    {levelLabels[sup.level]}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 text-muted-foreground">OT Hours</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right font-medium tabular-nums">
                    {sup.overtime_hours?.toLocaleString()}h
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 text-muted-foreground">Cost</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right font-medium text-green-600 tabular-nums">
                    ${sup.cost?.toLocaleString()}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-border">
                <td className="py-2 px-3 text-muted-foreground">Employees</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right font-medium tabular-nums">
                    {sup.employee_count}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="py-2 px-3 text-muted-foreground">Avg OT/Employee</td>
                {selectedSupervisors.map(sup => (
                  <td key={sup.supervisor_id} className="py-2 px-3 text-right font-medium tabular-nums">
                    {sup.avg_overtime_per_employee?.toFixed(1)}h
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    );
  };

  const renderLevelContent = (level) => {
    const levelData = data?.levels?.[level];
    if (!levelData) return null;

    const chartData = levelData.data.slice(0, 15);

    return (
      <div className="space-y-4">
        {/* Compare Button */}
        {selectedSupervisors.length >= 2 && !showComparison && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-end"
          >
            <Button
              onClick={() => setShowComparison(true)}
              className="bg-[#2E5C8A] hover:bg-[#1e3a5f]"
              data-testid="compare-supervisors-btn"
            >
              <GitCompare className="h-4 w-4 mr-2" />
              Compare {selectedSupervisors.length} Supervisors
            </Button>
          </motion.div>
        )}

        {/* Comparison View */}
        <AnimatePresence>
          {showComparison && renderComparisonView()}
        </AnimatePresence>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <UserCheck className="h-3.5 w-3.5" />
              Supervisors
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">
              {levelData.summary.total_supervisors}
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Users className="h-3.5 w-3.5" />
              Employees
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">
              {levelData.summary.total_employees}
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Clock className="h-3.5 w-3.5" />
              Total Hours
            </div>
            <div className="text-xl font-bold text-[#1e3a5f]">
              {levelData.summary.total_hours?.toLocaleString()}h
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" />
              Total Cost
            </div>
            <div className="text-xl font-bold text-green-600">
              ${levelData.summary.total_cost?.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 ? (
          <div className="bg-white rounded-lg border border-border p-4">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={chartData}
                layout="vertical"
                onClick={(e) => {
                  if (e?.activePayload?.[0]?.payload) {
                    handleBarClick(e.activePayload[0].payload);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8F0F8" />
                <XAxis type="number" tick={{ fill: '#1e3a5f', fontSize: 11 }} />
                <YAxis 
                  dataKey="supervisor_name" 
                  type="category" 
                  width={150}
                  tick={{ fill: '#1e3a5f', fontSize: 11 }}
                  tickFormatter={(value) => value.length > 20 ? value.slice(0, 20) + '...' : value}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="total_premium_hours" 
                  fill={LEVEL_COLORS[level]} 
                  name="Premium Hours"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No data available for this supervisor level</p>
          </div>
        )}

        {/* Top Supervisors Table with Checkboxes */}
        {chartData.length > 0 && (
          <div className="overflow-x-auto">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">
                Select supervisors to compare (max 4)
              </p>
              {selectedSupervisors.length > 0 && (
                <span className="text-sm font-medium text-[#2E5C8A]">
                  {selectedSupervisors.length} selected
                </span>
              )}
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="w-10 py-2 px-2"></th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    Supervisor
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    Title
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    OT Hours
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    Cost
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    Employees
                  </th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">
                    Avg/Emp
                  </th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {chartData.slice(0, 10).map((row, idx) => (
                  <tr
                    key={row.supervisor_id || `sup-${idx}`}
                    className={`border-b border-border hover:bg-primary/5 transition-colors ${
                      isSelected(row.supervisor_id) ? 'bg-blue-50' : ''
                    }`}
                    data-testid={`supervisor-row-${idx}`}
                  >
                    <td className="py-2 px-2">
                      <Checkbox
                        checked={isSelected(row.supervisor_id)}
                        onCheckedChange={() => handleCheckboxChange(row)}
                        data-testid={`supervisor-checkbox-${idx}`}
                      />
                    </td>
                    <td 
                      className="py-2 px-3 font-medium cursor-pointer hover:text-[#2E5C8A]"
                      onClick={() => handleBarClick(row)}
                    >
                      {row.supervisor_name}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{row.supervisor_title || '-'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{row.overtime_hours?.toLocaleString()}h</td>
                    <td className="py-2 px-3 text-right tabular-nums text-green-600">${row.cost?.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{row.employee_count}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{row.avg_overtime_per_employee?.toFixed(1)}h</td>
                    <td className="py-2 px-3">
                      <ChevronRight 
                        size={14} 
                        className="text-muted-foreground cursor-pointer hover:text-[#2E5C8A]"
                        onClick={() => handleBarClick(row)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="supervisor-analysis">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Supervisor Analysis
          </h3>
          <p className="text-sm text-muted-foreground">
            Premium pay by supervisor hierarchy level • Select to compare
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2E5C8A] mx-auto"></div>
            <p className="mt-3 text-muted-foreground text-sm">Loading supervisor data...</p>
          </div>
        </div>
      ) : data ? (
        <Tabs value={activeLevel} onValueChange={setActiveLevel}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="level_1" data-testid="supervisor-l1-tab">
              Direct Supervisor
              <span className="ml-1.5 text-xs bg-[#2E5C8A]/10 text-[#2E5C8A] px-1.5 py-0.5 rounded">
                {data.levels?.level_1?.summary?.total_supervisors || 0}
              </span>
            </TabsTrigger>
            <TabsTrigger value="level_2" data-testid="supervisor-l2-tab">
              Level 2
              <span className="ml-1.5 text-xs bg-[#1e3a5f]/10 text-[#1e3a5f] px-1.5 py-0.5 rounded">
                {data.levels?.level_2?.summary?.total_supervisors || 0}
              </span>
            </TabsTrigger>
            <TabsTrigger value="level_3" data-testid="supervisor-l3-tab">
              Level 3
              <span className="ml-1.5 text-xs bg-[#4CAF93]/10 text-[#4CAF93] px-1.5 py-0.5 rounded">
                {data.levels?.level_3?.summary?.total_supervisors || 0}
              </span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="level_1" className="mt-4">
            {renderLevelContent('level_1')}
          </TabsContent>
          <TabsContent value="level_2" className="mt-4">
            {renderLevelContent('level_2')}
          </TabsContent>
          <TabsContent value="level_3" className="mt-4">
            {renderLevelContent('level_3')}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p>No supervisor data available</p>
        </div>
      )}
    </div>
  );
};

export default SupervisorAnalysis;
